'use client'

import ImageWithFallback from '@/components/common/image-with-fallback'
import PersonIcon from '@/components/icons/person-icon'
import { CarClassBadge } from '@/components/results/car-class-badge'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { TableCell, TableRow } from '@/components/ui/table'
import { isColorLight } from '@/helpers/color-utils'
import {
  urlSafeProfileBasePath,
  urlSafeProfilePath,
} from '@/helpers/url-safe-profile-path'
import { getUserProfilePictureUrl } from '@/helpers/user'
import { getDriverDisplayIdentifierDisplay } from '@/lib/driver-display-identifiers'
import { cn } from '@/lib/utils'
import Link from 'next/link'
import StandingsDriverLapCell from '../cells/standings-driver-lap-cell'
import StandingsDriverLapsCell from '../cells/standings-driver-laps-cell'
import StandingsDriverPointsCell from '../cells/standings-driver-points-cell'
import StandingsDriverPosCell from '../cells/standings-driver-pos-cell'

export default function StandingsDriverTableRow({
  rowIndex,
  result,
  session,
  showTeams,
  showSplits,
  showCarClasses,
  showClassPosition,
  showLapCount,
  isSelected,
  driverDisplayIdentifier,
  timingOverride,
}) {
  const showPoints =
    (session &&
      (session.pointDistribution?.length > 0 ||
        session.metadata?.splitPointDistributions)) ||
    !session

  const isUnclaimed = result.user?.isUnclaimed
  const displayName = getDriverDisplayIdentifierDisplay(
    result.user,
    driverDisplayIdentifier,
  )

  const onRowClick = () => {
    if (isUnclaimed) return
    window.open(urlSafeProfileBasePath(result.user), '_blank')
  }

  return (
    <>
      <DesktopRow
        rowIndex={rowIndex}
        result={result}
        session={session}
        onRowClick={onRowClick}
        showTeams={showTeams}
        showSplits={showSplits}
        showCarClasses={showCarClasses}
        showClassPosition={showClassPosition}
        showLapCount={showLapCount}
        showPoints={showPoints}
        isSelected={isSelected}
        isUnclaimed={isUnclaimed}
        displayName={displayName}
        timingOverride={timingOverride}
      />

      <MobileRow
        rowIndex={rowIndex}
        result={result}
        session={session}
        showTeams={showTeams}
        showSplits={showSplits}
        showCarClasses={showCarClasses}
        showClassPosition={showClassPosition}
        showLapCount={showLapCount}
        showPoints={showPoints}
        isSelected={isSelected}
        isUnclaimed={isUnclaimed}
        displayName={displayName}
        timingOverride={timingOverride}
      />
    </>
  )
}

function DesktopRow({
  rowIndex,
  result,
  session,
  onRowClick,
  showTeams,
  showSplits,
  showCarClasses,
  showClassPosition,
  showLapCount,
  showPoints,
  isSelected,
  isUnclaimed,
  displayName,
  timingOverride,
}) {
  return (
    <TableRow
      className={cn(
        `hidden h-full grow md:table-row standings-user-${result.user?.id ?? 'unknown'}`,
        { 'cursor-pointer': !isUnclaimed },
        { 'animate-pulse': isSelected },
      )}
      onClick={onRowClick}
    >
      <TableCell>
        <StandingsDriverPosCell position={rowIndex + 1} />
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-3 font-semibold">
          <div className="relative size-10 min-w-10 overflow-hidden rounded-full">
            <ImageWithFallback
              src={getUserProfilePictureUrl(result.user, {
                profile: 'driver',
              })}
              fill
            />
          </div>
          <div className="flex flex-col">
            <span>
              {displayName}
              {isUnclaimed && (
                <sup className="ml-0.5 text-xs font-medium text-neutral-blue-400">
                  *
                </sup>
              )}
            </span>
          </div>
        </div>
      </TableCell>

      {showTeams && (
        <TableCell>
          <div className="flex flex-col">
            <span>{result.currentTeam?.name ?? '-'}</span>
            {result.previousTeams?.length > 0 && (
              <span className="text-muted-foreground text-xs">
                Prev: {result.previousTeams.map((team) => team.name).join(', ')}
              </span>
            )}
          </div>
        </TableCell>
      )}

      {showSplits && (
        <TableCell>
          <div className="flex items-start">
            {result.split ? (
              <div
                className="rounded-full px-4 py-2 text-p-1 font-semibold"
                style={{
                  backgroundColor: result.split.color ?? '#ffffff',
                  color: isColorLight(result.split.color ?? '#ffffff')
                    ? '#000000'
                    : '#ffffff',
                }}
              >
                {result.split.name}
              </div>
            ) : (
              '-'
            )}
          </div>
        </TableCell>
      )}

      {showCarClasses && (
        <TableCell>
          <CarClassBadge carClass={result.carClass} />
        </TableCell>
      )}

      {showClassPosition && (
        <TableCell>{result.classPosition ?? '-'}</TableCell>
      )}

      {showLapCount && (
        <TableCell>
          <StandingsDriverLapsCell laps={result.laps} />
        </TableCell>
      )}

      {session && (
        <TableCell>
          <StandingsDriverLapCell
            position={rowIndex + 1}
            {...result}
            {...timingOverride}
            penaltyTime={result.penaltyTime}
          />
        </TableCell>
      )}

      {showPoints && (
        <TableCell className="text-end">
          <StandingsDriverPointsCell
            position={rowIndex + 1}
            points={result.total}
          />
        </TableCell>
      )}
    </TableRow>
  )
}

function MobileRow({
  rowIndex,
  result,
  session,
  showTeams,
  showSplits,
  showCarClasses,
  showClassPosition,
  showPoints,
  showLapCount,
  isSelected,
  isUnclaimed,
  displayName,
  timingOverride,
}) {
  return (
    <TableRow
      className={cn(
        `flex h-full grow justify-between md:hidden standings-user-${result.user?.id ?? 'unknown'}`,
        { 'cursor-pointer': !isUnclaimed },
        { 'animate-pulse': isSelected },
      )}
    >
      <TableCell>
        <StandingsDriverPosCell position={rowIndex + 1} />
      </TableCell>

      <TableCell
        className="grow"
        colSpan={
          1 +
          Number(showTeams) +
          Number(showSplits) +
          Number(showCarClasses) +
          Number(showClassPosition) +
          Number(!!session) +
          Number(showLapCount)
        }
      >
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem
            value={result.user?.id ?? result.id ?? 'unknown'}
            className="m-0 border-none"
          >
            <AccordionTrigger>
              <div className="flex items-center gap-3 text-left font-semibold">
                <div className="relative flex size-10 min-w-10 items-center justify-center overflow-hidden rounded-full border border-neutral-blue-600 bg-neutral-blue-700">
                  {!isUnclaimed ? (
                    <ImageWithFallback
                      src={getUserProfilePictureUrl(result.user)}
                      fill
                    />
                  ) : (
                    <PersonIcon className="size-5 stroke-neutral-blue-300" />
                  )}
                </div>
                <div className="flex flex-col">
                  <span>
                    {displayName}
                    {isUnclaimed && (
                      <sup className="ml-0.5 text-xs font-medium text-neutral-blue-400">
                        *
                      </sup>
                    )}
                  </span>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4">
              {showTeams && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">Team</span>
                  <div className="flex flex-col items-end">
                    <span>{result.currentTeam?.name ?? '-'}</span>
                    {result.previousTeams?.length > 0 && (
                      <span className="text-muted-foreground text-xs">
                        Prev:{' '}
                        {result.previousTeams
                          .map((team) => team.name)
                          .join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {showSplits && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">Tier / Split</span>

                  <div className="flex items-start">
                    {result.split ? (
                      <div
                        className="rounded-full px-4 py-2 text-p-1 font-semibold"
                        style={{
                          backgroundColor: result.split.color ?? '#ffffff',
                        }}
                      >
                        {result.split.name}
                      </div>
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
              )}

              {showCarClasses && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">Car Class</span>
                  <CarClassBadge carClass={result.carClass} />
                </div>
              )}

              {showClassPosition && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">Class Position</span>
                  <span>{result.classPosition ?? '-'}</span>
                </div>
              )}

              {showLapCount && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">Laps</span>
                  <StandingsDriverLapsCell laps={result.laps} />
                </div>
              )}

              {showPoints && session && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">Time</span>
                  <StandingsDriverLapCell
                    position={rowIndex + 1}
                    {...result}
                    {...timingOverride}
                    penaltyTime={result.penaltyTime}
                  />
                </div>
              )}

              {!isUnclaimed && (
                <Button type="button" className="w-full" size="sm" asChild>
                  <Link
                    href={urlSafeProfilePath({
                      ...result.user,
                      profileType: 'driver',
                    })}
                    target="_blank"
                  >
                    View Profile
                  </Link>
                </Button>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </TableCell>

      {showPoints ? (
        <TableCell className="text-end">
          <StandingsDriverPointsCell
            position={rowIndex + 1}
            points={result.total}
          />
        </TableCell>
      ) : (
        session && (
          <TableCell className="text-end">
            <StandingsDriverLapCell
              position={rowIndex + 1}
              {...result}
              {...timingOverride}
              penaltyTime={result.penaltyTime}
            />
          </TableCell>
        )
      )}
    </TableRow>
  )
}
